import { getPreferenceFromCache, setPreference, deletePreference } from "./PreferenceStore";

type LocalStorageOneFromKey =
    | "actionPlan-main-tabs"
    | "actionPlan-trajectory-tabs"
    | "actionPlan-planning-tabs"
    | "actionPlan-catalog-tabs";

const actionPlanMainTabs = ["planning", "trajectory", "catalog"] as const;
export type ActionPlanMainTabs = (typeof actionPlanMainTabs)[number];

const actionPlanTrajectoryTabs = ["barChart", "cards"] as const;
export type ActionPlanTrajectoryTabs = (typeof actionPlanTrajectoryTabs)[number];

const actionPlanPlanningTabs = ["list"] as const;
export type ActionPlanPlanningTabs = (typeof actionPlanPlanningTabs)[number];

const actionPlanCatalogTabs = ["list"] as const;
export type ActionPlanCatalogTabs = (typeof actionPlanCatalogTabs)[number];

export const oneOfConfiguration = {
    "actionPlan-main-tabs": {
        values: actionPlanMainTabs,
        default: actionPlanMainTabs[0],
    },
    "actionPlan-trajectory-tabs": {
        values: actionPlanTrajectoryTabs,
        default: actionPlanTrajectoryTabs[0],
    },
    "actionPlan-planning-tabs": {
        values: actionPlanPlanningTabs,
        default: actionPlanPlanningTabs[0],
    },
    "actionPlan-catalog-tabs": {
        values: actionPlanCatalogTabs,
        default: actionPlanCatalogTabs[0],
    },
};

const TAB_PREFERENCE_KEY_MAP: Record<LocalStorageOneFromKey, string> = {
    "actionPlan-main-tabs": "actionPlan-main",
    "actionPlan-trajectory-tabs": "actionPlan-trajectory",
    "actionPlan-planning-tabs": "actionPlan-planning",
    "actionPlan-catalog-tabs": "actionPlan-catalog",
};

type AllConfigurationValues<T extends LocalStorageOneFromKey> = (typeof oneOfConfiguration)[T]["values"][number];

export class OneInLocalStorage {
    static getItem<T extends LocalStorageOneFromKey>(key: T): AllConfigurationValues<T> {
        const config = oneOfConfiguration[key];
        const preferenceKey = TAB_PREFERENCE_KEY_MAP[key];

        const cached = getPreferenceFromCache<string>("ui.tabs", preferenceKey);
        const item = cached ?? localStorage.getItem(key);

        if (item === null) {
            OneInLocalStorage.setItem(key, config.default);
            return config.default;
        }

        const existingItem = config.values.find((i) => i === item);
        const isItemDeprecated = existingItem === undefined;
        if (isItemDeprecated) {
            OneInLocalStorage.setItem(key, config.default);
            return config.default;
        }
        return existingItem;
    }

    static setItem<T extends LocalStorageOneFromKey>(key: T, value: AllConfigurationValues<T>): void {
        localStorage.setItem(key, value);

        const preferenceKey = TAB_PREFERENCE_KEY_MAP[key];
        setPreference("ui.tabs", preferenceKey, value);
    }

    static deleteItem(key: LocalStorageOneFromKey) {
        localStorage.removeItem(key);

        const preferenceKey = TAB_PREFERENCE_KEY_MAP[key];
        deletePreference("ui.tabs", preferenceKey);
    }
}
